import { z } from 'zod';
import { centAmount, isoDate, positiveAmount, uuid } from './posting.js';
/**
 * Die Verträge der Eröffnungsposten eines Vorsystems über HTTP — Auftrag
 * `LX-freigabeendpunkt` (2026-10-10), `E-1843`. Grundlage:
 * `docs/superpowers/specs/2026-09-29-lexware-migration-design.md`
 * Abschnitte 9.1 bis 9.3 (`D-30`, `D-51`, `D-57`); Tabelle
 * `eroeffnung_posten` (Migration `0300`); der Dienst ist
 * `apps/api/src/vorsystem/eroeffnung.ts`.
 *
 * **Drei Endpunkte, und keiner legt eine Eröffnung an.** Angelegt werden
 * Eröffnungen ausschliesslich vom Läufer (`pnpm vorsystem:lexware`, Schritt
 * `eroeffnen`) aus dem festgeschriebenen offenen Betrag des Archivbelegs; eine
 * Maske, in die ein Mensch einen Eröffnungsbetrag tippt, gäbe es nur um den
 * Preis einer zweiten, nicht nachgerechneten Zahl. Über HTTP gehen die Liste,
 * die Freigabe (bucht) und die Anlage einer Ausbuchung (bucht nicht).
 *
 * **Die Zustände spiegeln die `CHECK`-Bedingungen aus `0300`**
 * (`eroeffnung_posten_art_bekannt`, `…_richtung_bekannt`, `…_status_bekannt`)
 * und die Typen in `eroeffnung.ts` — dieselben Werte, keine Übersetzung.
 */
/** Eröffnung (aus dem Lauf) oder Ausbuchung (Gegenzeile mit Grund, `D-51`). */
export const eroeffnungspostenArtSchema = z.enum(['eroeffnung', 'ausbuchung']);
/** Rechnung (Forderungskonto im Soll) oder Gutschrift (im Haben) — folgt der Belegart. */
export const eroeffnungspostenRichtungSchema = z.enum(['forderung', 'gutschrift']);
/** `angelegt` → `freigegeben`; die Freigabe ist eine Folgezeile (anfügend, `0300`). */
export const eroeffnungspostenStatusSchema = z.enum(['angelegt', 'freigegeben']);
/**
 * Ein Vorgang der Arbeitsliste `GET /api/v1/vorsystem/eroeffnungsposten` —
 * seine LETZTE Zeile. `id` ist immer die Kennung der **Anlage** und damit der
 * Wert, den Freigabe und Ausbuchung im Pfad erwarten; `zeileId` ist die
 * Kennung der gezeigten Zeile (bei einem freigegebenen Vorgang die Folgezeile).
 *
 * **Kein Kundenname, keine Anschrift** — wie jeder Weg dieser Ära (`E-1685`):
 * Kennung und Nummer des Belegs genügen, um ihn zu finden.
 */
export const eroeffnungspostenZeileSchema = z
    .object({
    id: uuid,
    zeileId: uuid,
    art: eroeffnungspostenArtSchema,
    richtung: eroeffnungspostenRichtungSchema,
    documentId: uuid,
    belegnummer: z.string(),
    /** Echt positiv (`eroeffnung_posten_betrag_positiv`); die Richtung trägt das Vorzeichen. */
    betrag: positiveAmount,
    /** Der Stichtag (`einrichtung.journalbeginn`), auch bei einer Ausbuchung. */
    buchungsdatum: isoDate,
    /** Nur an einer Ausbuchung: die Anlage der Eröffnung, die sie mindert. */
    bezugPostenId: uuid.nullable(),
    begruendung: z.string(),
    status: eroeffnungspostenStatusSchema,
    angelegtVon: uuid,
    angelegtAm: z.string(),
    journalEntryId: uuid.nullable(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /** Einzelfreigabe (`0263`): der Anleger selbst, nur unter einer Regel ohne vier Augen. */
    einzelfreigabe: z.boolean(),
})
    .strict();
/**
 * Die drei Buchungsformen der Eröffnung (Spec Lexware-Migration 9.2 und 16,
 * `D-99`; Spalte `vorsystem_projekt.eroeffnung_buchungsform`, Migration
 * `0350`, dieselben Werte wie die Bedingung `vorsystem_projekt_eroeffnungsform_bekannt`):
 *
 * | Form | Buchung der Eröffnung |
 * |---|---|
 * | `bilanzsicht` | Forderung an Saldenvorträge (`9000`); die spätere Zahlung bucht Bank an Forderung ohne Zusatzbuchung |
 * | `erloesvortrag` | Forderung an ein gewähltes Erlöskonto (Klasse 8): die Einnahme steht im Jahr der Eröffnung |
 * | `zufluss_umbuchung` | Forderung an `9000`, bei Zahlung eine zweite Buchung an das Erlöskonto — noch nicht verfügbar |
 */
export const EROEFFNUNGSFORMEN = ['bilanzsicht', 'erloesvortrag', 'zufluss_umbuchung'];
export const eroeffnungsformWahlSchema = z.enum(EROEFFNUNGSFORMEN);
/** Ein Erlöskonto des SKR03: vier Ziffern, Klasse 8 (Bedingung `vorsystem_projekt_eroeffnungskonto_form`). */
export const erloeskontoSchema = z.string().regex(/^8[0-9]{3}$/, 'Ein Erlöskonto hat vier Ziffern und beginnt mit 8');
/**
 * `PUT /api/v1/vorsystem/eroeffnungsform` — der Rumpf. Das Gegenkonto gehört nur
 * zu `erloesvortrag` und ist dort Pflicht; bei den beiden anderen Formen ist es
 * `9000`, und die Angabe eines Kontos wäre widersprüchlich.
 */
export const eroeffnungsformEingabeSchema = z
    .object({
    buchungsform: eroeffnungsformWahlSchema,
    gegenkonto: erloeskontoSchema.nullable().optional(),
})
    .strict()
    .superRefine((wert, ctx) => {
    const hatKonto = wert.gegenkonto !== undefined && wert.gegenkonto !== null;
    if (wert.buchungsform === 'erloesvortrag' && !hatKonto) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['gegenkonto'], message: 'Beim Erlösvortrag ist ein Erlöskonto zu wählen' });
    }
    if (wert.buchungsform !== 'erloesvortrag' && hatKonto) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['gegenkonto'], message: 'Nur der Erlösvortrag nimmt ein Erlöskonto' });
    }
});
/**
 * Die geltende Einstellung samt dem, was eine Oberfläche dazu wissen muss.
 * `buchungsform: null` heisst: nicht gewählt, es wird nichts freigegeben.
 * `gesperrt`: Es ist schon eine Eröffnung gebucht, die Wahl ist fest.
 * `zuflussUmbuchungVerfuegbar`: ob `zufluss_umbuchung` wählbar ist.
 */
export const eroeffnungsformSchema = z
    .object({
    buchungsform: eroeffnungsformWahlSchema.nullable(),
    gegenkonto: z.string().nullable(),
    gesperrt: z.boolean(),
    zuflussUmbuchungVerfuegbar: z.boolean(),
})
    .strict();
/**
 * `GET /api/v1/vorsystem/eroeffnungsposten` — die volle Antwort.
 *
 * `buchungsform` nennt die geltende Buchungsform der Eröffnung oder `null`,
 * solange keine gewählt ist (`LX-6`, `D-99`) oder die gewählte noch nicht
 * verfügbar ist. Mit `null` weist jede Freigabe mit
 * `VORSYSTEM_BUCHUNGSFORM_OFFEN` ab — eine Oberfläche liest es hier, statt es an
 * einer Ablehnung zu erraten. `eroeffnungsform` ist die Einstellung selbst.
 */
export const eroeffnungspostenListeSchema = z
    .object({
    posten: z.array(eroeffnungspostenZeileSchema),
    buchungsform: z.string().nullable(),
    eroeffnungsform: eroeffnungsformSchema,
})
    .strict();
/**
 * `POST /api/v1/vorsystem/eroeffnungsposten/:id/freigabe` — die Antwort. Kein
 * Rumpf auf der Anfrageseite: Was freigegeben wird, sagt der Pfad, wer
 * freigibt, die Sitzung.
 */
export const eroeffnungspostenFreigabeAntwortSchema = z
    .object({
    id: uuid,
    journalEntryId: uuid,
})
    .strict();
/**
 * `POST /api/v1/vorsystem/eroeffnungsposten/:id/ausbuchung` — die Anlage einer
 * Ausbuchung gegen die Eröffnung im Pfad. **Ein Grund ist Pflicht** (`D-51`,
 * `eroeffnung_posten_begruendung_gefuellt`); ein Grund aus Leerzeichen ist
 * keiner. Das Buchungsdatum steht nicht im Rumpf: Eine Ausbuchung bucht auf
 * das Buchungsdatum ihrer Eröffnung.
 */
export const eroeffnungAusbuchungAnlageSchema = z
    .object({
    betrag: positiveAmount,
    begruendung: z.string().trim().min(1, 'Eine Ausbuchung braucht einen Grund').max(2000),
})
    .strict();
/** Die Antwort der Anlage: die Kennung der neuen Ausbuchung (ihre Anlage). */
export const eroeffnungAusbuchungAntwortSchema = z
    .object({
    id: uuid,
})
    .strict();
/**
 * `POST /api/v1/vorsystem/eroeffnungsposten/freigabe` — die Sammelfreigabe der
 * Eröffnungen (Spec Lexware-Migration 9.2, Nachtrag `D-102`). Ohne `postenIds`
 * werden alle angelegten, noch nicht freigegebenen **Eröffnungen** freigegeben;
 * mit `postenIds` nur diese (Kennungen der Anlagen, wie die Liste sie nennt).
 * Ausbuchungen gehören nie dazu — sie werden einzeln freigegeben.
 */
export const eroeffnungspostenSammelfreigabeSchema = z
    .object({
    postenIds: z.array(uuid).min(1, 'Die Liste der Posten ist leer').max(10000).optional(),
})
    .strict();
/** Ein Posten, den die Sammelfreigabe gebucht hat. */
export const eroeffnungspostenSammelGebuchtSchema = z
    .object({
    /** Die Kennung der Anlage. */
    id: uuid,
    /** Die Kennung der Freigabezeile. */
    zeileId: uuid,
    journalEntryId: uuid,
    belegnummer: z.string(),
    richtung: eroeffnungspostenRichtungSchema,
    betrag: positiveAmount,
})
    .strict();
/**
 * Ein Posten, den die Sammelfreigabe nicht gebucht hat: `code` und `meldung`
 * sind genau die Ablehnung, die die Einzelfreigabe für denselben Posten gäbe
 * (etwa `VORSYSTEM_POSTEN_APPROVAL_SELF`). Der Posten bleibt angelegt.
 * `belegnummer` und `betrag` sind nur bei einer unbekannten Kennung `null`.
 */
export const eroeffnungspostenSammelAbgewiesenSchema = z
    .object({
    id: uuid,
    belegnummer: z.string().nullable(),
    betrag: positiveAmount.nullable(),
    code: z.string(),
    meldung: z.string(),
})
    .strict();
/**
 * Die Antwort der Sammelfreigabe — ein Ergebnis je Posten, nicht alles oder
 * nichts. `summe` ist der Saldo der gebuchten Eröffnungen auf dem
 * Forderungskonto: Rechnungen minus Gutschriften (als Zeichenkette). Ist nichts
 * mehr angelegt, sind beide Listen leer und `summe` ist `0.00`.
 */
export const eroeffnungspostenSammelfreigabeAntwortSchema = z
    .object({
    anzahlGebucht: z.number().int().nonnegative(),
    anzahlAbgewiesen: z.number().int().nonnegative(),
    summe: centAmount,
    gebucht: z.array(eroeffnungspostenSammelGebuchtSchema),
    abgewiesen: z.array(eroeffnungspostenSammelAbgewiesenSchema),
})
    .strict();
//# sourceMappingURL=vorsystem-eroeffnung.js.map